/**
 * Casino Games CSV export (CASENG-960).
 *
 * Client-side serialisation of the filtered/sorted catalog. UTF-8 BOM + CRLF
 * so Excel on Windows opens game names with accents without a re-encode step.
 */

import type { CasinoConfigGameRow } from "../../../../utils/game-utils/gamesUtils";
import { formatGameIdForDisplay } from "../../../../utils/game-utils/gamesUtils";

export const CASINO_GAMES_CSV_HEADERS = [
  "Game Name",
  "Game ID",
  "Provider",
  "Sub-provider",
  "Status",
] as const;

const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;
const FILENAME_SLUG_MAX = 40;

export type CasinoGamesCsvRow = {
  gameName: string;
  gameId: string;
  provider: string;
  subProvider: string;
  status: "Active" | "Inactive";
};

export function escapeCasinoGamesCsvCell(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  const safe = FORMULA_TRIGGERS.test(raw) ? `'${raw}` : raw;
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

export function mapCasinoGameToCsvRow(game: CasinoConfigGameRow): CasinoGamesCsvRow {
  const gameName =
    String(game.name || "").trim() || String(game.game_name || "").trim();
  return {
    gameName,
    gameId: formatGameIdForDisplay(game),
    provider: String(game.provider ?? ""),
    subProvider: String(game.sub ?? ""),
    status: game.is_active !== false ? "Active" : "Inactive",
  };
}

export function buildCasinoGamesCsv(games: CasinoConfigGameRow[]): string {
  const headerLine = CASINO_GAMES_CSV_HEADERS.map(escapeCasinoGamesCsvCell).join(
    ",",
  );
  const lines: string[] = [
    headerLine,
    ...games.map((game) => {
      const row = mapCasinoGameToCsvRow(game);
      return [
        escapeCasinoGamesCsvCell(row.gameName),
        escapeCasinoGamesCsvCell(row.gameId),
        escapeCasinoGamesCsvCell(row.provider),
        escapeCasinoGamesCsvCell(row.subProvider),
        escapeCasinoGamesCsvCell(row.status),
      ].join(",");
    }),
  ];
  return `${lines.join("\r\n")}\r\n`;
}

/** Lowercase search slug for filenames: spaces → hyphens, max 40 chars. */
export function slugifyCasinoGamesCsvFilter(search: string): string {
  return search
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .slice(0, FILENAME_SLUG_MAX)
    .replace(/^-+|-+$/g, "");
}

/** Local calendar date YYYY-MM-DD (not UTC). */
export function formatCasinoGamesCsvLocalDate(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * `casino-games[_filter]_YYYY-MM-DD.csv`
 * Filter segment omitted when search is empty.
 */
export function buildCasinoGamesCsvFilename(
  search = "",
  date: Date = new Date(),
): string {
  const day = formatCasinoGamesCsvLocalDate(date);
  const slug = slugifyCasinoGamesCsvFilter(search);
  return slug
    ? `casino-games_${slug}_${day}.csv`
    : `casino-games_${day}.csv`;
}

/** Triggers a browser download; BOM is prepended once before the CSV body. */
export function downloadCasinoGamesCsv(csv: string, filename: string): void {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
